import { useEffect, useState } from 'react'
import { Fragment } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Circle, Check, X } from 'phosphor-react'
import { toast } from 'react-toastify'

import api from '../../../services/api'

import { errorApi } from '../../../utils/errorApi'

import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner'

import styles from './styles.module.css'
import { filterDeliveryRequestsByInternalTransfers } from '../../../utils/internalTransfers'

const rolesMap = {
  manager: { api: 'delivery-manager', web: 'gestor-entregas' },
  attendant: { api: 'attendant', web: 'atendente' },
}

const statusColor = {
  on_route: '#053C5E',
  delivered: 'green',
  canceled: 'red',
}

export const DeliveryFlow = ({
  highlightedRequestId,
  role,
  onRequestSelect = '',
  showBorder = true,
  deliveryId = 0,
  vehicleId = 0,
  circleContent = 'order',
  preloadedData = null,
}) => {
  const [requests, setRequests] = useState([])
  const [delivery, setDelivery] = useState()

  const { isLoading, setIsLoading } = useLoading()

  useEffect(() => {

    if (preloadedData) {
      const { requests, ...deliv } = preloadedData;
      setDelivery(deliv);
      setRequests(filterDeliveryRequestsByInternalTransfers(requests));
      return;
    }

    async function loadData() {
      setIsLoading(true)
      try {
        let deliveryData

        if (vehicleId) {
          // se vehicleId foi passado, delivery será a última delivery do veículo com status !== in_loading
          const { data } = await api.get(
            `/${rolesMap[role].api}/deliveries?requestsData=true&vehicleId=${vehicleId}&creationOrder=desc&limit=5`,
          )
          if (data && data.length) {
            deliveryData =
              data.find(
                (delivery) => delivery.status !== 'in_loading' && delivery.requests?.length > 0,
              ) ?? data.find((delivery) => delivery.requests?.length > 0)
          }
        } else {
          // se não, deliveryId específico (busca delivery específica)
          const { data } = await api.get(
            `${rolesMap[role].api}/deliveries/${deliveryId}?requestsData=true`,
          )
          deliveryData = data
        }

        if (deliveryData) {
          const { requests, ...deliv } = deliveryData
          setDelivery(deliv)
          setRequests(filterDeliveryRequestsByInternalTransfers(requests))
        }
      } catch (err) {
        toast.error(
          errorApi(err, 'Falha ao buscar os dados da entrega! Por favor, tente novamente.'),
        )
      }
      setIsLoading(false)
    }

    loadData()
  }, [role, deliveryId, vehicleId, setIsLoading, preloadedData])

  /* centralizar request destacada no container, se existe */
  useEffect(() => {
    if (highlightedRequestId) {
      setTimeout(() => {
        const container = document.getElementById('scrollContainer')
        const targetElement = document.getElementById(`request_${highlightedRequestId}`)

        if (container && targetElement) {
          const containerWidth = container.clientWidth
          const targetOffsetLeft = targetElement.offsetLeft
          const targetWidth = targetElement.clientWidth

          // Calcula a posição de scroll para centralizar o elemento
          const scrollLeft = targetOffsetLeft - containerWidth / 2 + targetWidth / 2
          container.scrollLeft = scrollLeft
        }
      }, 500) // Atraso de 500ms para que o código seja executado após a renderização/montagem do DOM
    }
  }, [highlightedRequestId])

  if (isLoading) {
    return (
      <LoadingSpinner text="Buscando informações sobre a carga de requisições de entrega. Aguarde..." />
    )
  }

  if (!delivery) {
    return (
      <div className="text-center">
        <span className={`${styles.modalInfoBadge} text-center`}>
          O veículo não possui nenhuma carga de entregas recente.
        </span>
      </div>
    )
  }

  if (delivery.status === 'in_loading') {
    return (
      <div className="text-center">
        <span className={`${styles.modalInfoBadge}`}>
          O veículo não está em rota de entrega no momento (está em carregamento).
        </span>
      </div>
    )
  }

  return (
    <Row
      className={`${styles.container}`}
      style={showBorder ? { border: '1px solid #ccc' } : {}}
      id="scrollContainer"
    >
      {requests &&
        requests.length > 0 &&
        requests.map((request, index) => (
          <Fragment key={`frag_${index}`}>
            {/* Linha de conexão entre as colunas */}
            {index !== 0 && (
              <p
                className={styles.line}
                style={{ minWidth: `${16 + requests.length * 156}px` }}
              ></p>
            )}

            {/* Coluna de cada entrega */}
            <Col
              id={`request_${request.id}`}
              className={`${styles.requestCol} ${
                request.id === highlightedRequestId ? styles.highlightedRequestCol : ''
              }`}
              style={
                request.id === highlightedRequestId
                  ? {
                      borderLeft: `5px solid ${
                        request.status === 'delivered'
                          ? 'green'
                          : request.status === 'on_route'
                            ? '#053C5E'
                            : 'red'
                      }`,
                    }
                  : {}
              }
              onClick={() => {
                if (onRequestSelect) {
                  onRequestSelect(request)
                }
              }}
            >
              <span>
                {/* Status de Entrega com cor condicional */}
                <p
                  className={styles.statusP}
                  style={{
                    color:
                      request.status === 'delivered'
                        ? 'green'
                        : request.status === 'on_route'
                          ? '#053C5E'
                          : 'red',
                  }}
                >
                  {request.status === 'delivered'
                    ? 'Entregue'
                    : request.status === 'on_route'
                      ? 'Em rota'
                      : 'Cancelada'}
                </p>
              </span>

              {/* Ícones ou Número de pedido dependendo de circleContent */}
              <div
                className={styles.circleDiv}
                style={{ backgroundColor: statusColor[request.status] }}
              >
                {circleContent === 'order' ? (
                  <span className={styles.orderNumber}>{request.order}</span>
                ) : request.status === 'delivered' ? (
                  <Check className={styles.innerCircle} size="26px" weight="fill" color="white" />
                ) : request.status === 'canceled' ? (
                  <X className={styles.innerCircle} size="26px" weight="bold" color="white" />
                ) : (
                  <Circle className={styles.innerCircle} size="26px" weight="fill" color="white" />
                )}
              </div>
              {/* Nome do recebedor, com estilo destacado caso seja o elemento em destaque */}
              <p
                className={styles.receiverName}
                style={
                  request.id === highlightedRequestId
                    ? {
                        fontWeight: 'bolder',
                        color:
                          request.status === 'delivered'
                            ? 'green'
                            : request.status === 'on_route'
                              ? '#053C5E'
                              : 'red',
                      }
                    : {}
                }
              >
                {request.receiver_name}
              </p>
            </Col>
          </Fragment>
        ))}
    </Row>
  )
}
